import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getTag, addTag, updateTag, clearCurrentTag } from '../actions/tagActions';
import TagForm from '../components/content-management/TagForm';
import AdminLayout from '../components/settings/AdminLayout';

import { X } from 'lucide-react';
import { isAdminRole } from '../utils/roleHelpers';

const TagEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentTag, tagLoading } = useSelector(state => state.tag);
  const { user } = useSelector(state => state.auth);

  // Determine mode based on URL
  const isViewMode = location.pathname.includes('/view');
  const isEditMode = location.pathname.includes('/edit');
  const isNewMode = !id;

  const isAdmin = isAdminRole(user?.role);

  useEffect(() => {
    const loadData = async () => {
      try {
        // If we have an ID, load the tag
        if (id) {
          await dispatch(getTag(id));
        }
      } catch (error) {
        console.error('Error loading tag:', error);
      }
    };

    loadData();

    // Cleanup on unmount
    return () => {
      dispatch(clearCurrentTag());
    };
  }, [dispatch, id]);

  const handleSubmit = async (tagData) => {
    try {
      const action = id ? updateTag(id, tagData) : addTag(tagData);
      await dispatch(action);
      // Redirect back to tags list
      navigate('/settings', { state: { activeTab: 'Tags' } });
    } catch (error) {
      console.error('Error saving tag:', error);
    }
  };

  const handleClose = () => {
    navigate('/settings', { state: { activeTab: 'Tags' } });
  };

  // Show loading state
  if (tagLoading && id) {
    return (
      <AdminLayout activeTab="Tags">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="h-8 w-48 bg-gray-200 rounded animate-shimmer"></div>
            <button
              type="button"
              onClick={() => navigate('/settings', { state: { activeTab: 'Tags' } })}
              className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form Skeleton */}
          <div className="space-y-6">
            <div>
              <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-shimmer"></div>
              <div className="h-10 bg-gray-200 rounded animate-shimmer"></div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // If trying to edit/view a tag that doesn't exist
  if (id && !currentTag && !tagLoading) {
    return (
      <AdminLayout activeTab="Tags">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-red-500 text-xl mb-4">Tag not found</div>
          <button
            onClick={() => navigate('/settings', { state: { activeTab: 'Tags' } })}
            className="px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors"
          >
            Return to Tags
          </button>
        </div>
      </AdminLayout>
    );
  }

  const mode = isViewMode ? 'view' : isEditMode ? 'edit' : 'new';

  return (
    <AdminLayout activeTab="Tags">
      <TagForm
        mode={mode}
        tag={currentTag}
        onSubmit={handleSubmit}
        onClose={handleClose}
      />
    </AdminLayout>
  );
};

export default TagEditor;
